import React, { useCallback, useMemo, useState } from "react"
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router";
import { ensureArray } from "cityads-v3/utils/arrayUtils_ts"
import { RootState } from "cityads-v3/types/reducer";
import { Offer } from "cityads-v3/types/offers";
import { PROPERTY_ITEM_SLICE } from '../offers/Offers';
import classes from '../offers/Offers.scss';

import ImgNSWithFallback from 'cityads-v3/components/ImgWithFallback/ImgNSWithFallback';
import { Link } from "react-router-dom";
import DateFormat from "cityads-v3/components/Format/DateFormat";
import Tooltip from 'cityads-v3/components/Tooltip';
import { Coupon } from "cityads-v3/types/coupon";
import { useDialogActions } from "cityads-v3/hooks/dialog";

export const PROMO_ACTION_TYPE_PROMOCODE = 6;
const TABLE_FIELDS_MOBILE = ['favicon', 'name', 'offer'];


export const useOrderKeys = () => {
  const { t } = useTranslation(['Offers'])

  return useMemo(() => ([
    {
      name: 'id',
      title: t('table.id'),
      defaultAsc: true,
      hidden: true
    },
    {
      name: 'offer_name',
      title: t('table.offer_name'),
      defaultAsc: true,
    },
    {
      name: 'start_date',
      title: t('table.start_date'),
      defaultAsc: false,
    },
    {
      name: 'end_date',
      title: t('table.end_date'),
      defaultAsc: false,
    },
    {
      name: 'promo_action_type',
      title: t('table.promo_action_type'),
      defaultAsc: true,
    }
  ]), [t])
}


/**
 * Описание столбцов для табличного представления купонов
 */
type useTableColsProps = {
  onCouponGeoClick: (evt: React.MouseEvent, geos: string) => void
}

export const useCouponsTableCols = ({ onCouponGeoClick }: useTableColsProps) => {
  const { t } = useTranslation(['Offers'])
  const getOfferLink = useGetOfferLink()
  const couponOffersById = useSelector((state: RootState) => state.offers.couponOffersById)
  const promoActionTypesById = useSelector((state: RootState) => state.offers.promoActionTypesById)
  const promoCouponScopesById = useSelector((state: RootState) => state.offers.promoCouponScopesById)
  const regionsById = useSelector((state: RootState) => state.offers.regionsById)
  const promoCouponLanguagesById = useSelector((state: RootState) => state.language.sadukaLanguagesById)
  const isMobile = useSelector((state: RootState) => state.app.isMobile)

  return useMemo(() => (
    [
      {
        name: 'name',
        title: t('table.couponName'),
      },
      {
        name: 'favicon',
        dense: true,
        formatter: (value: unknown, row: Coupon) => {
          const offer = couponOffersById[row.offer];
          const littleFavicon = offer?.favicon?.[0]?.source || null;

          return <ImgNSWithFallback
            className={classes.offerFavicon}
            src={littleFavicon}
            fallbackSrc={require('../offers/img/default_avatar.svg')}
            alt={offer?.name}
            width="16"
            height="16"
          />;
        }
      },
      {
        name: 'offer',
        title: t('table.offer_name'),
        formatter: (value: number) => {
          const offer = couponOffersById[value];

          if (offer) {
            const link = getOfferLink(value);
            const title = couponOffersById[value].translated_name || couponOffersById[value].name;

            return (
              <Link className={classes.offers__table_link} to={link}>{title}</Link>
            );
          }

          return '—';
        }
      },
      {
        name: 'start_date',
        title: t('table.start_date_short'),
        formatter: (value: Coupon['start_date']) => {
          return <DateFormat date={value} />
        }
      },
      {
        name: 'end_date',
        title: t('table.end_date_short'),
        formatter: (value: Coupon['end_date']) => {
          return <DateFormat date={value} />
        }
      },
      {
        name: 'promo_action_type',
        title: t('table.promo_action_type'),
        overflow: 'initial',
        formatter: (value: Coupon['promo_action_type'], row: Coupon) => {
          const type = promoActionTypesById[value]?.title;

          if (+value === PROMO_ACTION_TYPE_PROMOCODE) {
            return <Tooltip title={row.promocode}><span>{type}</span></Tooltip>;
          }

          return type;
        }
      },
      {
        name: 'promo_coupon_scope',
        title: t('table.promo_coupon_scope'),
        overflow: 'initial',
        formatter: (value: Coupon['promo_coupon_scope']) => promoCouponScopesById[value]?.title
      },
      {
        name: 'geos',
        title: t('region'),
        formatter: (value: Coupon['geos']) => {
          if (!value) return '';

          const geos = (value || [])
            .map(geo => regionsById[geo]?.name)
            .join(', ');

          const shortTitle = (value || []).slice(0, PROPERTY_ITEM_SLICE)
            .map(geo => regionsById[geo]?.name)
            .join(', ') + (
              value.length > PROPERTY_ITEM_SLICE ? ('..[' + value.length + '+]') : ''
            );

          return <span className={classes.tableLink} onClick={(evt) => onCouponGeoClick(evt, geos)}>{shortTitle}</span>;
        }
      },
      {
        name: 'language_id',
        title: t('table.couponLanguage'),
        formatter: (value: Coupon['language_id']) => {
          if (!value) return '';

          return ensureArray(value).map(id => promoCouponLanguagesById[id] ? promoCouponLanguagesById[id].title : '-').join(', ');
        }
      }
    ].filter(item => {
      if (isMobile) {
        return TABLE_FIELDS_MOBILE.indexOf(item.name) !== -1;
      }
      return true;
    })
  ), [
    t,
    getOfferLink,
    onCouponGeoClick,
    couponOffersById,
    promoActionTypesById,
    promoCouponScopesById,
    regionsById,
    promoCouponLanguagesById,
    isMobile
  ]);
}


const useGetOfferLink = () => {
  const location = useLocation()

  return useCallback((id: Offer['id']) => ({
    pathname: `/publisher/offer/${id}`,
    state: {
      source: 'coupons',
      sourceSearch: location.search,
    },
  }), [location])
}


export const useGeoDialog = () => {
  const [opened, open, close] = useDialogActions()
  const [geos, setGeos] = useState<string>('')

  const setAndOpen = useCallback((evt: React.MouseEvent, geos: string) => {
    evt.preventDefault();
    setGeos(geos)
    open();
  }, [open, setGeos])

  return [opened, setAndOpen, close, geos]
}

type ExportPopupType = 'json' | 'xml';
export const useExportPopup = () => {
  const [opened, open, close] = useDialogActions()
  const [type, setType] = useState<ExportPopupType>(null);

  const setAndOpen = useCallback((type: ExportPopupType) => {
    setType(type);
    open();
  }, [setType, open])

  return [opened, setAndOpen, close, type]
}

export const useTotalText = () => {
  const { t } = useTranslation(['Offers'])
  const withFilters = useSelector((state: RootState) => state.offers.metaCoupons.withFilters)
  const total = useSelector((state: RootState) => state.offers.metaCoupons.total)
  const isLoadingCoupons = useSelector((state: RootState) => state.offers.isLoadingCoupons)

  return useMemo(() => {
    if (withFilters && !isLoadingCoupons) {
      return t('totalCouponText', { count: total })
    }

    return null
  }, [withFilters, isLoadingCoupons, total, t])
}

export const useCouponExportOfferInfo = () => {
  const couponsById = useSelector((state: RootState) => state.offers.couponsById)
  const couponOffersById = useSelector((state: RootState) => state.offers.couponOffersById)

  return useCallback((couponId: number) => {
    const coupon = couponsById?.[couponId]
    const offer = couponOffersById?.[coupon?.offer];

    if (!offer) {
      return {}
    }

    return {
      offerId: offer.id,
      offerName: offer.name,
      offerIsDeeplinkEnabled: offer.is_deeplink_enabled,
    }
  }, [couponsById, couponOffersById])
}
